import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import {fmtYYYYMMDDHHmmss} from '_logics/LFCFormatUtil';
import {useParseWithSettings} from '_logics/LFCParseUtil';
import dayjs from 'dayjs';
import {isEmpty} from 'lodash';
import {GraphBaseData, GraphSelectedData, SearchType} from 'pages/lq-job/cloud-distance/types';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {blue, red} from '@mui/material/colors';

interface Series {
  name: string;
  type: string;
  data: number[] | Array<number[]>;
  color: string;
}

interface CloudDistanceMatchBoxChartProps {
  title: string;
  source: GraphBaseData;
  searchType: SearchType;
  exportFilename?: string;
  height?: string;
  zoomChartId?: string;
  zoomedChartId?: string;
  zoomCharts?: (zoomChartId?: string) => void;
  zoomChartsClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
  onPointClick?: (data: GraphSelectedData) => void;
  onClickDetailAnalysis?: (data: GraphSelectedData) => void;
}

export const CloudDistanceMatchBoxChart = ({
  title,
  source,
  searchType,
  exportFilename,
  height,
  zoomChartId,
  zoomedChartId,
  zoomCharts,
  zoomChartsClose,
  onPointClick,
  onClickDetailAnalysis
}: CloudDistanceMatchBoxChartProps) => {
  const {t} = useTranslation();
  const {parseSize} = useParseWithSettings();

  const [series, setSeries] = useState<Series[]>([]);
  const [exportFields, setExportFields] = useState<Record<string, any>>({});
  const [exportData, setExportData] = useState<any>([]);

  const CalcQ = useCallback((data: number[], coefficient: number): number => {
    const data_s = data.sort((a: number, b: number) => a - b);
    const pos = (data_s.length - 1) * coefficient;
    const base = Math.floor(pos);
    const rest = pos - base;
    if (data_s[base + 1] !== undefined) {
      return data_s[base] + rest * (data_s[base + 1] - data_s[base]);
    } else {
      return data_s[base];
    }
  }, []);

  const CalcQuartile = useCallback((data: number[]): Array<number> => {
    if (data.length === 0) {
      return [];
    }
    const min = Math.min(...data.map((a: any) => Number(a)));
    const q1 = CalcQ(data, 0.25);
    const q2 = CalcQ(data, 0.5);
    const q3 = CalcQ(data, 0.75);
    const max = Math.max(...data.map((a: any) => Number(a)));

    // [min,  Q1,  median (or Q2),  Q3,  max]
    return [min, q1, q2, q3, max];
  }, []);

  useEffect(() => {
    if (isEmpty(source)) return;

    if (searchType === SearchType.WORK) {
      setSeries([
        {
          name: t('最大値'),
          type: 'line',
          data: source.data_upper_distance as number[],
          color: red[500]
        },
        {
          name: t('最小値'),
          type: 'line',
          data: source.data_lower_distance as number[],
          color: blue[500]
        }
      ]);
    } else {
      const tmpOutPut: Array<Array<number[]>> = [[], []];

      source.xlist.forEach((xstr: string, i: number) => {
        const uval = source.data_upper_distance[i] as number[];
        tmpOutPut[0].push(isEmpty(uval) ? [] : CalcQuartile(uval));

        const lval = source.data_lower_distance[i] as number[];
        tmpOutPut[1].push(isEmpty(lval) ? [] : CalcQuartile(lval));
      });

      setSeries([
        {
          name: t('最大値'),
          type: 'boxplot',
          data: tmpOutPut[0],
          color: red[500]
        },
        {
          name: t('最小値'),
          type: 'boxplot',
          data: tmpOutPut[1],
          color: blue[500]
        }
      ]);
    }
  }, [source]);

  useEffect(() => {
    if (searchType === SearchType.WORK) {
      //検査ごと
      setExportFields({
        for_export: t('検査日時'),
        serial: t('シリアルナンバー'),
        map: t('検査部位'),
        data_upper_distance: t('最大値'),
        data_lower_distance: t('最小値')
      });
    } else {
      setExportFields({
        for_export: t('検査日時'),
        map: t('検査部位'),
        x0: `${t('最大値')}-${t('最大')}`,
        x1: `${t('最大値')}-${t('Q3')}`,
        x2: `${t('最大値')}-${t('中央(orQ2)')}`,
        x3: `${t('最大値')}-${t('Q1')}`,
        x4: `${t('最大値')}-${t('最小')}`,
        y0: `${t('最小値')}-${t('最大')}`,
        y1: `${t('最小値')}-${t('Q3')}`,
        y2: `${t('最小値')}-${t('中央(orQ2)')}`,
        y3: `${t('最小値')}-${t('Q1')}`,
        y4: `${t('最小値')}-${t('最小')}`
      });
    }
  }, [searchType]);

  useEffect(() => {
    if (series.length !== 2) return;

    if (searchType === SearchType.WORK) {
      //検査ごと
      const _exportData = source.xlist.map((xval: string, i: number) => {
        return {
          for_export: fmtYYYYMMDDHHmmss(source.detail[i].lq_time),
          serial: source.detail[i].serial || source.detail[i].uuid,
          map: source.map,
          data_upper_distance: parseSize(source.data_upper_distance[i] as number),
          data_lower_distance: parseSize(source.data_lower_distance[i] as number)
        };
      });
      setExportData(_exportData);
    } else {
      const _exportData: any = [];

      (series[0].data as Array<number[]>).forEach((maxdatas, i) => {
        const mindatas = series[1].data[i] as number[];
        if (isEmpty(maxdatas) || isEmpty(mindatas)) {
          return;
        }

        _exportData.push({
          for_export: fmtYYYYMMDDHHmmss(source.xlist[i]),
          map: source.map,
          x0: parseSize(maxdatas[4]),
          x1: parseSize(maxdatas[3]),
          x2: parseSize(maxdatas[2]),
          x3: parseSize(maxdatas[1]),
          x4: parseSize(maxdatas[0]),
          y0: parseSize(mindatas[4]),
          y1: parseSize(mindatas[3]),
          y2: parseSize(mindatas[2]),
          y3: parseSize(mindatas[1]),
          y4: parseSize(mindatas[0])
        });
      });
      setExportData(_exportData);
    }
  }, [series]);

  return (
    <div>
      <LFCBaseCharts
        option={{
          title: {
            text: title
          },
          grid: {
            top: 60,
            bottom: 60,
            right: 5,
            left: 60
          },
          legend: {
            type: 'scroll',
            bottom: 0
          },
          tooltip: {
            trigger: 'item',
            formatter: function (params: any) {
              if (params.seriesType === 'line') {
                return `${params.name}<br>
                    <br>
                    <span style="font-weight:bold">${t('検査日時')}</span>: ${dayjs(
                      source.detail[params.dataIndex].lq_time
                    ).format('YYYY-MM-DD HH:mm:ss')}<br/>
                    <span style="font-weight:bold">${t('シリアルナンバー')}</span>: ${
                      source.detail[params.dataIndex].serial || source.detail[params.dataIndex].uuid
                    }<br/>
                    <span style="font-weight:bold">${
                      params.seriesName === '最大値' ? t('最大値') : t('最小値')
                    }</span>: ${parseSize(params?.value || 0)}
                    <br/>
                    `;
              } else {
                // [min,  Q1,  median (or Q2),  Q3,  max]
                return `${params.name}<br/>
                  ${params.seriesName}<br/>
                  <br/>
                  <span style="font-weight:bold">${t('最大')}</span>: ${parseSize(
                    params?.value[5] || 0
                  )}<br/>
                  <span style="font-weight:bold">Q3</span>: ${parseSize(params?.value[4] || 0)}<br/>
                  <span style="font-weight:bold">${t('中央')} (or Q2)</span>: ${parseSize(
                    params?.value[3] || 0
                  )}<br/>
                  <span style="font-weight:bold">Q1</span>: ${parseSize(params?.value[2] || 0)}<br/>
                  <span style="font-weight:bold">${t('最小')}</span>: ${parseSize(
                    params?.value[1] || 0
                  )}`;
              }
            }
          },
          dataZoom: [
            {
              type: 'inside'
            }
          ],
          xAxis: {
            type: 'category',
            name: '',
            data: source.xlist,
            axisLabel: {
              rotate: '20',
              fontSize: 10
            }
          },
          yAxis: {
            type: 'value',
            name: '(mm)'
          },
          series: series
        }}
        exportData={exportData}
        exportFields={exportFields}
        exportFilename={exportFilename}
        height={height ? height : '100px'}
        onEvents={{
          click: (params: any) => {
            if (onPointClick != null) {
              params.mapName = source.map;
              onPointClick(params);
            }
          }
        }}
        zoomCharts={zoomCharts ? zoomCharts : undefined}
        zoomChartsClose={zoomChartsClose ? zoomChartsClose : undefined}
        zoomChartId={zoomChartId ? zoomChartId : undefined}
        zoomStatus={zoomChartId === zoomedChartId}
        isShowDetailAnalysis={true}
        onClickDetailAnalysis={() => {
          if (onClickDetailAnalysis != null) {
            const params = {mapName: source.map};
            onClickDetailAnalysis(params);
          }
        }}
      />
    </div>
  );
};
